import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { apiGet, EVENTO_SISTEMA_EM_PAUSA } from '../services/api';

/**
 * Pausa do sistema: o aviso de Configurações › Avisos marcado como
 * "Pausar o sistema". Quem decide é o servidor — ele responde 503 a tudo no
 * horário da pausa e recusa o login. Esta tela só deixa isso claro:
 *
 * - na pausa, quem não é administrador vê a tela inteira "Sistema em pausa",
 *   com a mensagem e a hora de volta; o administrador vê só uma faixa;
 * - nas 24 horas antes, uma faixa avisa quando a pausa começa;
 * - quando a pausa acaba (ou o administrador encerra antes), a página
 *   recarrega sozinha, para as telas buscarem de novo o que o 503 barrou.
 *
 * Pergunta ao servidor a cada minuto, e na hora em que alguma requisição
 * volta com 503 de pausa (evento disparado por services/api.js).
 */

const INTERVALO_MS = 60 * 1000;

const formatarHora = (data) => new Date(data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
const formatarData = (data) => {
  const d = new Date(data);
  const hoje = new Date();
  const amanha = new Date(hoje);
  amanha.setDate(hoje.getDate() + 1);
  if (d.toDateString() === hoje.toDateString()) return `hoje às ${formatarHora(d)}`;
  if (d.toDateString() === amanha.toDateString()) return `amanhã às ${formatarHora(d)}`;
  return `${d.toLocaleDateString('pt-BR')} às ${formatarHora(d)}`;
};

const faltam = (ms) => {
  const min = Math.max(0, Math.ceil(ms / 60000));
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const resto = min % 60;
  return resto ? `${h} h ${resto} min` : `${h} h`;
};

export const valeParaOPerfil = (pausa, role) => !pausa.perfis?.length || pausa.perfis.includes(role);

function FaixaDaPausa({ cor, titulo, children }) {
  return (
    <div role="status" className="flex items-start gap-3 rounded-xl px-4 py-3 shadow-lg mb-4"
      style={{ background: cor, color: '#ffffff' }}>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 flex-shrink-0 mt-0.5" fill="none"
        viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <div className="text-sm leading-snug min-w-0 flex-1">
        <div className="font-bold">{titulo}</div>
        <div className="whitespace-pre-line break-words">{children}</div>
      </div>
    </div>
  );
}

/** A tela inteira. `embutida`: a prévia em Configurações › Avisos. */
export function TelaDePausa({ pausa, agora, onSair, darkMode, embutida = false }) {
  return (
    <div role={embutida ? undefined : 'alertdialog'} aria-modal={embutida ? undefined : 'true'}
      aria-labelledby={embutida ? undefined : 'pausa-titulo'}
      className={`${embutida ? 'absolute' : 'fixed z-[200]'} inset-0 flex items-center justify-center p-4 overflow-y-auto`}
      style={{
        background: darkMode
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #f8fafc 100%)',
      }}>
      <div className={`relative w-full max-w-[480px] rounded-3xl border px-8 py-10 text-center overflow-hidden ${darkMode
        ? 'bg-slate-800/80 border-slate-600/15 text-slate-200'
        : 'bg-white/90 border-slate-200 shadow-xl text-slate-700'}`}>
        <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'linear-gradient(90deg, #F59E0B, #E83A3C)' }} />
        <img src="/LogoNome.svg" alt="SIDEB" className="w-40 h-auto mx-auto mb-6" />
        <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center bg-[#F59E0B]/15 text-[#F59E0B]">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-9 w-9" fill="none" viewBox="0 0 24 24"
            stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 id={embutida ? undefined : 'pausa-titulo'} className="text-2xl font-bold mb-2">Sistema em pausa</h1>
        {pausa.mensagem && (
          <p className={`text-sm whitespace-pre-line break-words mb-5 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            {pausa.mensagem}
          </p>
        )}
        <div className={`rounded-xl px-4 py-3 mb-6 ${darkMode ? 'bg-slate-900/60' : 'bg-slate-100'}`}>
          <div className={`text-xs uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Volta</div>
          <div className="text-lg font-semibold">{formatarData(pausa.fim)}</div>
          <div className={`text-xs mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            daqui a {faltam(new Date(pausa.fim).getTime() - agora)} · a página volta sozinha
          </div>
        </div>
        <button type="button" onClick={onSair}
          className={`px-5 py-2.5 rounded-lg text-sm font-medium border ${darkMode
            ? 'border-slate-600 text-slate-300 hover:bg-slate-700'
            : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          Sair
        </button>
      </div>
    </div>
  );
}

function PausaDoSistema({ darkMode }) {
  const { user, logout } = useAuth();
  const [situacao, setSituacao] = useState({ ativas: [], proximas: [] });
  const [agora, setAgora] = useState(() => Date.now());
  const estavaEmPausa = useRef(false);

  const buscar = useCallback(() => {
    apiGet('/pausa/')
      .then((dados) => {
        setSituacao({ ativas: dados?.ativas || [], proximas: dados?.proximas || [] });
        setAgora(Date.now());
      })
      // Sem resposta, fica o que já se sabia: o servidor barra de qualquer jeito.
      .catch(() => {});
  }, []);

  useEffect(() => {
    buscar();
    const id = setInterval(buscar, INTERVALO_MS);
    window.addEventListener(EVENTO_SISTEMA_EM_PAUSA, buscar);
    return () => {
      clearInterval(id);
      window.removeEventListener(EVENTO_SISTEMA_EM_PAUSA, buscar);
    };
  }, [buscar]);

  const role = user?.role;
  const admin = role === ROLES.ADMIN;
  const todas = useMemo(() => [...situacao.ativas, ...situacao.proximas]
    .filter((p) => valeParaOPerfil(p, role)), [situacao, role]);
  const ativa = todas
    .filter((p) => new Date(p.inicio).getTime() <= agora && new Date(p.fim).getTime() > agora)
    .sort((a, b) => new Date(b.fim) - new Date(a.fim))[0];
  const proxima = todas
    .filter((p) => new Date(p.inicio).getTime() > agora)
    .sort((a, b) => new Date(a.inicio) - new Date(b.inicio))[0];

  // Relógio da tela: a pausa entra e sai no minuto certo, sem esperar a
  // próxima consulta; e o "daqui a X min" anda.
  useEffect(() => {
    if (!ativa && !proxima) return undefined;
    const id = setInterval(() => setAgora(Date.now()), 15 * 1000);
    return () => clearInterval(id);
  }, [ativa, proxima]);

  const bloqueado = !!ativa && !admin;

  // Acabou a pausa: recarrega, para as telas refazerem o que o 503 barrou.
  useEffect(() => {
    if (estavaEmPausa.current && !bloqueado) window.location.reload();
    estavaEmPausa.current = bloqueado;
  }, [bloqueado]);

  // Com a tela de pausa por cima, a página de trás não rola.
  useEffect(() => {
    if (!bloqueado) return undefined;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = anterior; };
  }, [bloqueado]);

  if (bloqueado) return <TelaDePausa pausa={ativa} agora={agora} onSair={logout} darkMode={darkMode} />;

  if (ativa) {
    const quem = ativa.perfis?.length ? 'Os perfis da pausa estão' : 'Os demais usuários estão';
    return (
      <FaixaDaPausa cor="#E83A3C" titulo={`Sistema em pausa até ${formatarData(ativa.fim)}`}>
        {quem} sem acesso. Você continua entrando porque é administrador — para liberar antes,
        encerre a pausa em Configurações › Avisos.{ativa.mensagem ? `\n${ativa.mensagem}` : ''}
      </FaixaDaPausa>
    );
  }

  if (proxima) {
    return (
      <FaixaDaPausa cor="#B45309"
        titulo={`Pausa programada: ${formatarData(proxima.inicio)} até ${formatarData(proxima.fim)}`}>
        {admin
          ? 'Nesse horário só administradores entram no sistema.'
          : `Daqui a ${faltam(new Date(proxima.inicio).getTime() - agora)} o sistema fica fora do ar. Salve seus lançamentos antes.`}
        {proxima.mensagem ? `\n${proxima.mensagem}` : ''}
      </FaixaDaPausa>
    );
  }

  return null;
}

export default PausaDoSistema;
